import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Flex,
    SimpleGrid,
    Heading,
    Text,
    Icon,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    VStack
} from '@chakra-ui/react';
import { AiOutlineHdd, AiOutlineReload, AiOutlineTable } from 'react-icons/ai';
import { Loading } from '../../../../components/loading';
import StaticButton from '../../../../components/UI/StaticButton';
import Api from '../../../../services/api';
import DatabaseUsageCard from '../../../../components/UI/DatabaseUsageCard';

const SQLServerMonitorPage = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    
    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const res = await Api.get('/monitordb/data/sqlserver'); 
            setData(res.data);
        } catch (error) {
            console.error("Erro ao carregar dados do monitor SQL:", error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    if (loading && !data) return <Loading />;
    if (!data) return (
        <Flex justify="center" align="center" h="80vh" p={6}>
            <Text color="gray.400">Não foi possível carregar os dados de monitoramento.</Text>
        </Flex>
    );
    
    const { cards, grid, lastUpdate } = data;
    const serverDiskFreeMB = cards.serverDiskSpaceMB;
    const sortedDatabases = [...cards.databases].sort((a, b) => (b.porcentagemUsado || 0) - (a.porcentagemUsado || 0));

    return (
        <VStack spacing={8} align="stretch" p={0}>
            <Flex align="center" justify="space-between" mb={6} borderBottom="1px solid" borderColor="gray.800" pb={4} direction={{ base: 'column', md: 'row' }} gap={4}>
                <Heading as="h1" size="lg" color="white">Monitor do Banco de Dados</Heading>
                <Flex gap={4} align="center" w={{ base: '100%', md: 'auto' }} justify="space-between">
                    <Text fontSize="sm" color="gray.500" whiteSpace="nowrap">
                        Atualizado: {new Date(lastUpdate).toLocaleTimeString()}
                    </Text>
                    <StaticButton 
                        icon={AiOutlineReload} 
                        onClick={fetchData} 
                        isLoading={loading}
                        h="2.5rem"
                    >
                        Atualizar
                    </StaticButton>
                </Flex>
            </Flex>

            <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} spacing={6}>
                <Box 
                    bg="surface" 
                    p={5} 
                    borderRadius="lg" 
                    border="1px solid" 
                    borderColor="gray.800" 
                    minH="190px"
                    _hover={{ borderColor: "green.400", boxShadow: "0 0 10px 2px rgba(72,187,120,0.3)" }}
                >
                    <Flex align="center" mb={4}>
                        <Icon as={AiOutlineHdd} boxSize={8} color="green.400" mr={4} />
                        <Heading as="h4" size="md" color="white">Disco do Servidor</Heading>
                    </Flex>
                    <Text fontSize="sm" color="gray.400">Espaço Livre (C:)</Text>
                    <Text fontSize="3xl" fontWeight="bold" color="green.400">
                        {(serverDiskFreeMB / 1024).toFixed(2)} GB
                    </Text>
                </Box>

                {sortedDatabases.map((db, index) => (
                    <DatabaseUsageCard 
                        key={index} 
                        db={db} 
                        color="green.400"   
                    />
                ))}
            </SimpleGrid>

            <Box bg="surface" p={6} borderRadius="lg" border="1px solid" borderColor="gray.800">
                <Flex align="center" mb={4}> 
                    <Icon as={AiOutlineTable} color="secondary" boxSize={6} mr={3}/>
                    <Heading as="h3" size="md" color="white"> 
                        Top 50 Tabelas por Uso de Espaço
                    </Heading>
                </Flex>
                <Box overflowX="auto">
                    <Table variant="simple" size="sm">
                        <Thead>
                            <Tr>
                                <Th color="secondary" borderBottomColor="gray.700">#</Th>
                                <Th color="secondary" borderBottomColor="gray.700">Nome da Tabela</Th>
                                <Th color="secondary" isNumeric borderBottomColor="gray.700">Registros</Th>
                                <Th color="secondary" isNumeric borderBottomColor="gray.700">Tamanho (MB)</Th>
                                <Th color="secondary" isNumeric borderBottomColor="gray.700">Índices (MB)</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {(grid.topTables || []).map((table, index) => (
                                <Tr key={index} _hover={{ bg: 'gray.800' }}>
                                    <Td borderBottomColor="gray.700" width="50px" color="green.400">{index + 1}</Td>
                                    <Td borderBottomColor="gray.700" maxW="300px" isTruncated title={table.TableName} color="green.400">{table.TableName}</Td>
                                    <Td isNumeric borderBottomColor="gray.700" color="green.400">{(table.RowCounts || 0).toLocaleString('pt-BR')}</Td>
                                    <Td isNumeric borderBottomColor="gray.700" color="green.400">{parseFloat(table.TotalSpaceMB || 0).toFixed(2)}</Td>
                                    <Td isNumeric borderBottomColor="gray.700" color="green.400">{parseFloat(table.IndexSpaceMB || 0).toFixed(2)}</Td>
                                </Tr>
                            ))}
                        </Tbody>
                    </Table>
                </Box>
            </Box>
        </VStack>
    );
};

export default SQLServerMonitorPage;